'use client';
import { FC, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Select,
  Autocomplete,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import Link from 'next/link';
import { ArrowForward, Visibility } from '@mui/icons-material';
import { useBackend } from '@/hooks/useBackend';
import { invalidateSampleGridCache } from '@/lib/cache/sampleGridCache';
import { PrivilegeEnum, ReportingCategoryDto, SampleDto, StatusDto, SubstrateDto } from '@/api/ApiClient';
import { Footer } from '@/components/details/Footer';
import { log } from '@/lib/utils/logger';
import { formatDate } from '@/lib/utils/dateUtils';
import DetailField, { FIELD_WIDTH } from '@/components/common/DetailField';
import { FormLabel } from '@/components/common/FormLabel';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { findTechniqueById } from '@/lib/utils/optionUtils';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { sampleHasSubstrateDetailsData } from '@/lib/utils/sampleUtils';
import {
  filterSubstrateRows,
  pickRepresentativeSubstrateRow,
  samplePatchFromSubstrateRow,
  uniqueSortedDropdownStrings,
} from '@/lib/utils/substrateLookup';

import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useSnackbar } from '@/hooks/useSnackbar';
import StartingMaterialDetailsDialog from '@/components/details/StartingMaterialDetailsDialog';
import { useIsDataModified, useSampleData } from '@/hooks/useTreeDetailSubscriptions';
import { useTreeStore } from '@/stores/treeStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useAuthStore } from '@/stores/authStore';
import { useSampleReportingCategoryVisible } from '@/hooks/useSampleReportingCategoryVisible';
import { SampleOwnershipFields } from '@/components/details/SampleOwnershipFields';

export const selectOptions: OptionKey[] = [
  'sampleDropdownChoices',
  'researcherOptions',
  'facultyOptions',
  'materials',
  'substrateDropdownChoices',
  'substrates',
  'synthesisTechniques',
  'synthesisTechniquesLeafOnly',
  'projects',
  'projectType',
  'sampleReportingCategory',
];

const SampleDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const systemConfig = useSystemConfig();
  const { resolveUser } = useUserResolution();
  const backend = useBackend();

  const { setDataModified, updateDataPartial, updateData } = useTree();
  const { selectedNode } = useTreeSelection();
  const currentEntityId = useAuthStore((s) => s.currentEntityId);
  const currentEntityType = useAuthStore((s) => s.currentEntityType);
  const { showSnackbar } = useSnackbar();
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const hasProcessDevAccess = isAuthorized(PrivilegeEnum.AccessProcessDevelopmentSample, 'read');

  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const [startingMaterialModalOpen, setStartingMaterialModalOpen] = useState(false);
  const [selectedStartingMaterial, setSelectedStartingMaterial] = useState<SampleDto | null>(null);

  const sample = useSampleData(selectedNode);

  const displayProjectLabels = useMemo(() => {
    const labels = (sample?.projectLabels ?? []).filter((l): l is string => !!l);
    if (labels.length > 0) {
      return labels;
    }
    // Snapshots: unpublished project is omitted by the API — do not invent a project from tree context.
    if (sample?.isSnapshot) {
      return [];
    }
    // Opened from a project tree: list/reload payloads may omit labels briefly; context still knows the project.
    if (currentEntityType === 'project' && currentEntityId) {
      return [currentEntityId];
    }
    return [];
  }, [sample?.projectLabels, sample?.isSnapshot, currentEntityId, currentEntityType]);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  const getMaterialName = (id: number) => {
    const material = options.materials?.find((m) => m.id === id);
    return material?.label ?? material?.name ?? id.toString();
  };
  const getTechniqueName = (id: string) => findTechniqueById(options.synthesisTechniques, id)?.name || id;
  const materialOptions = useMemo(
    () =>
      options.materials
        ?.filter((m) => m.id !== undefined && m.name !== undefined)
        .sort((a, b) => a.name!.localeCompare(b.name!)) // We filtered null out above, so we can safely use ! here
        .map((m) => ({
          id: m.id!,
          name: m.name!,
        })),
    [options.materials]
  );
  const sd = options.substrateDropdownChoices;

  const substrateVendorOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.vendors), [sd?.vendors]);

  const substrateLotOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.lots), [sd?.lots]);

  const substrateNameOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.names), [sd?.names]);

  const substrateOrientationOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.orientations), [sd?.orientations]);

  const substrateRows = options.substrates;

  const applySubstrateLookupIfUnique = (
    matches: SubstrateDto[],
    extra: Partial<SampleDto> = {}
  ): Partial<SampleDto> | null => {
    if (matches.length !== 1) return null;
    return { ...samplePatchFromSubstrateRow(matches[0]), ...extra };
  };

  /** Grown layer ids: top layer first, numbered (no duplicate “A on B” line; no sample-material flags on sample). */
  const grownLayerMaterialsReadonly = (() => {
    if (!sample?.materialIds?.length) return null;
    const ids = sample.materialIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    if (!ids.length) return null;

    const nameFor = (id: number) => {
      const material = options.materials?.find((m) => m.id === id);
      return material?.label ?? material?.name ?? id.toString();
    };

    const byBottomFirst = ids;
    const topFirst = [...byBottomFirst].reverse();

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {topFirst.map((id, idx) => (
          <Typography key={`${id}-${idx}`} variant="body2" component="div" sx={{ '& sub, & sup': { lineHeight: 1 } }}>
            <Box component="span">{byBottomFirst.length - idx}: </Box>
            <Box component="span" dangerouslySetInnerHTML={{ __html: nameFor(id) }} />
          </Typography>
        ))}
      </Box>
    );
  })();

  // Check if parent node is a shipping node
  const isParentShipping = (() => {
    if (!selectedNode?.parentId) return false;
    const parentNode = useTreeStore.getState().nodes.get(selectedNode.parentId);
    return parentNode?.type === 'shipping';
  })();

  const { showReportingCategory, isReportingCategoryContextLoading } = useSampleReportingCategoryVisible(
    sample ?? undefined,
    hasProcessDevAccess,
    {
      projects: options.projects,
      projectTypes: options.projectType,
      optionsLoading,
    }
  );

  if (!sample || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  if (optionsLoading) {
    return <Typography variant="body1">Loading sample details...</Typography>;
  }

  const techniqueUsesSubstrates =
    options.synthesisTechniquesLeafOnly?.find((m) => m.id === sample.techniqueId)?.growOnSubstrate ?? true;

  // Check if the sample is submitted
  const isSubmitted = sample.status === StatusDto.Submitted;

  const handleSave = async () => {
    if (!sample.id) return;
    try {
      const saved = await backend.sample.update(sample.sampleLabel!, sample);
      invalidateSampleGridCache();
      // Update the data in the store
      updateData(selectedNode.dataId, saved);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      showSnackbar('Sample details saved successfully', 'success');
    } catch (error) {
      log.error('[SampleDetails] Failed to save sample:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save sample details.', 'error');
    }
  };

  const handleModification = (modified: Partial<SampleDto>) => {
    updateDataPartial(selectedNode.dataId, modified);
  };

  const handleViewStartingMaterial = (startingMaterial: SampleDto) => {
    setSelectedStartingMaterial(startingMaterial);
    setStartingMaterialModalOpen(true);
  };

  const handleCloseStartingMaterialModal = () => {
    setStartingMaterialModalOpen(false);
    setSelectedStartingMaterial(null);
  };

  // Type guard for startingMaterials array - API returns array but type definition is incorrect
  const startingMaterials = Array.isArray(sample.sampleAsSubstrate)
    ? sample.sampleAsSubstrate
    : sample.sampleAsSubstrate
      ? [sample.sampleAsSubstrate]
      : [];

  // Determine if we should show shipping link (sample has shipping ID and parent is not shipping)
  const shouldShowShippingLink = sample.shipping && !isParentShipping && displayProjectLabels.length > 0;

  // Check if sample is root (no parent)
  const isRootSample = !selectedNode?.parentId;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            General Details
          </Typography>
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* ID Field */}
                {isRootSample || !isReadOnly ? (
                  <DetailField label="ID" value={sample.sampleLabel || sample.id} isReadOnly required />
                ) : (
                  <Box sx={{ width: FIELD_WIDTH }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      <FormLabel required={false} isEditMode={false}>
                        ID
                      </FormLabel>
                    </Typography>
                    <Link
                      href={`${systemConfig?.basePath}/samples/${sample.sampleLabel || sample.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {sample.sampleLabel || sample.id}
                      </Typography>
                    </Link>
                  </Box>
                )}

                {/* Alias */}
                {!isReadOnly || sample.refSampleLabel ? (
                  <DetailField label="Alias" value={sample.refSampleLabel} isReadOnly={isReadOnly || isSubmitted}>
                    <TextField
                      value={sample.refSampleLabel}
                      onChange={(e) => {
                        const newAlias = e.target.value;
                        handleModification({ refSampleLabel: newAlias });
                      }}
                      fullWidth
                      size="small"
                      disabled={isReadOnly || isSubmitted}
                      slotProps={{ input: { readOnly: isReadOnly || isSubmitted } }}
                    />
                  </DetailField>
                ) : null}

                {/* Project / data-package ownership */}
                {sample ? (
                  <SampleOwnershipFields
                    sample={sample}
                    projectLabels={displayProjectLabels}
                    basePath={systemConfig?.basePath}
                  />
                ) : null}

                {/* Technique Section */}
                {sample.techniqueId ? (
                  <DetailField
                    label="Technique"
                    value={getTechniqueName(sample.techniqueId)}
                    isReadOnly={isReadOnly || isSubmitted}
                  >
                    <Select
                      value={sample.techniqueId}
                      onChange={(e) => {
                        const newTechniqueId = e.target.value;
                        handleModification({ techniqueId: newTechniqueId });
                      }}
                      fullWidth
                      size="small"
                      slotProps={{
                        input: { readOnly: isReadOnly }, // This controls whether the dropdown is editable or not
                      }}
                    >
                      {options.synthesisTechniques?.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </DetailField>
                ) : null}

                {/* Owner Section (userId → real name) */}
                {sample.userId ? (
                  <Box sx={{ width: FIELD_WIDTH }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Made by
                    </Typography>
                    <Typography variant="body1">{resolveUser(sample.userId)}</Typography>
                  </Box>
                ) : null}

                {/* Reporting Category — in-house / unassigned only; submitted samples may be recategorized */}
                {!sample.isSnapshot &&
                !isReportingCategoryContextLoading &&
                showReportingCategory &&
                (sample.reportingCategory?.id || !isReadOnly) ? (
                  <DetailField
                    label="Reporting Category"
                    value={sample.reportingCategory?.label}
                    isReadOnly={isReadOnly}
                  >
                    <Autocomplete<ReportingCategoryDto>
                      options={options.sampleReportingCategory ?? []}
                      value={
                        options.sampleReportingCategory?.find((cat) => cat.id === sample.reportingCategory?.id) ?? null
                      }
                      onChange={(_, newReportingCategory) => {
                        handleModification({
                          reportingCategory: newReportingCategory
                            ? {
                                id: newReportingCategory.id,
                                code: newReportingCategory.code,
                                label: newReportingCategory.label,
                              }
                            : undefined,
                        });
                      }}
                      getOptionLabel={(option) => option.label || option.code || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      disabled={isReadOnly}
                    />
                  </DetailField>
                ) : null}

                {/* Growth Date Section */}
                {sample.creationDate ? (
                  <Box sx={{ width: FIELD_WIDTH }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Inventory Date
                    </Typography>
                    <Typography variant="body1">{formatDate(sample.creationDate)}</Typography>
                  </Box>
                ) : null}

                {/* Sample Location Section */}
                {sample.sampleLocation ? (
                  <Box sx={{ width: FIELD_WIDTH }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sample Location
                    </Typography>
                    <Typography variant="body1">{sample.sampleLocation}</Typography>
                  </Box>
                ) : null}

                {/* Quality Section */}
                {sample.quality || !isReadOnly ? (
                  <DetailField label="Quality" value={sample.quality} isReadOnly={isReadOnly}>
                    <TextField
                      value={sample.quality}
                      onChange={(e) => {
                        const newQuality = e.target.value;
                        handleModification({ quality: newQuality });
                      }}
                      fullWidth
                      size="small"
                      slotProps={{ input: { readOnly: isReadOnly } }}
                    />
                  </DetailField>
                ) : null}

                {/* Size Section */}
                {sample.size || !isReadOnly ? (
                  <DetailField label="Size" value={sample.size} isReadOnly={isReadOnly || isSubmitted}>
                    <Autocomplete
                      options={options.sampleDropdownChoices?.sizes ?? []}
                      value={sample.size || ''}
                      onInputChange={(_, newInputValue) => {
                        handleModification({ size: newInputValue });
                      }}
                      onChange={(_, newValue) => {
                        handleModification({ size: newValue ?? undefined });
                      }}
                      freeSolo
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </DetailField>
                ) : null}

                {/* Thickness */}
                {sample.thickness || !isReadOnly ? (
                  <DetailField label="Thickness" value={sample.thickness} isReadOnly={isReadOnly || isSubmitted}>
                    <Autocomplete
                      options={options.sampleDropdownChoices?.thicknesses ?? []}
                      value={sample.thickness}
                      onInputChange={(_, newInputValue) => {
                        handleModification({ thickness: newInputValue });
                      }}
                      onChange={(_, newValue) => {
                        handleModification({ thickness: newValue ?? undefined });
                      }}
                      freeSolo
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </DetailField>
                ) : null}

                {/* Sample Holder Section */}
                {sample.sampleHolder || !isReadOnly ? (
                  <DetailField label="Sample Holder" value={sample.sampleHolder} isReadOnly={isReadOnly}>
                    <Autocomplete
                      options={options.sampleDropdownChoices?.sampleHolders ?? []}
                      value={sample.sampleHolder}
                      onChange={(_, newValue) => {
                        handleModification({ sampleHolder: newValue ?? undefined });
                      }}
                      freeSolo
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </DetailField>
                ) : null}

                {/* Grown Layer Material(s) — full row after normal-width fields (names may contain HTML) */}
                {grownLayerMaterialsReadonly ? (
                  <Box sx={{ width: { xs: '100%', sm: '100%' }, flexBasis: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Grown Layer Material(s)
                    </Typography>
                    {grownLayerMaterialsReadonly}
                  </Box>
                ) : null}

                {/*
                 * Sample Description - a bit hacky, but it often is just "Split from abc".
                 * Since we are adding that as alink below anyway, let's not show it again.
                 */}
                {!isReadOnly ||
                (sample.description &&
                  !(sample.splitFrom && sample.description === `Split from ${sample.splitFrom.sampleLabel}`)) ? (
                  <DetailField
                    label="Description"
                    value={<span dangerouslySetInnerHTML={{ __html: sample.description as TrustedHTML }} />}
                    isReadOnly={isReadOnly}
                    width={{ width: '100%' }}
                  >
                    <RichTextEditor
                      value={sample.description ?? ''}
                      onChange={(content) => {
                        log.debug('[SampleDetails] Description changed', content);
                        handleModification({ description: content });
                      }}
                    />
                  </DetailField>
                ) : null}

                {sample.splitFrom ? (
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Split from
                      </Typography>
                      <Link href={`${systemConfig?.basePath}/samples/${sample.splitFrom.sampleLabel}`} passHref>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" component="span">
                            <ArrowForward />
                          </IconButton>
                          <Typography variant="subtitle2" color="text.secondary">
                            {sample.splitFrom.sampleLabel}
                          </Typography>
                        </Box>
                      </Link>
                    </Box>
                  </Box>
                ) : null}

                {shouldShowShippingLink ? (
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Shipping
                      </Typography>
                      <Link
                        href={`${systemConfig?.basePath}/shippings/${encodeURIComponent(sample.shipping!.toString())}`}
                        passHref
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" component="span">
                            <ArrowForward />
                          </IconButton>
                          <Typography variant="subtitle2" color="text.secondary">
                            {sample.shipping}
                          </Typography>
                        </Box>
                      </Link>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </Paper>

          {/* StartingMaterials Section */}
          {startingMaterials.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                StartingMaterials
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {startingMaterials.map((startingMaterial: SampleDto, index: number) => (
                  <Paper
                    key={startingMaterial.id}
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      flex: {
                        xs: '1 1 100%', // Full width on extra small screens
                        sm: '1 1 100%', // Full width on small screens
                        md: startingMaterials.length > 1 ? '1 1 calc(50% - 8px)' : '1 1 100%', // Side-by-side on medium+ screens if 2 startingMaterials
                      },
                      minWidth: { xs: 0, md: startingMaterials.length > 1 ? '300px' : 'auto' },
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {startingMaterials.length > 1 ? `StartingMaterial #${index + 1}` : 'StartingMaterial'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {startingMaterial.sampleLabel}
                            {startingMaterial.refSampleLabel ? (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({startingMaterial.refSampleLabel})
                              </Typography>
                            ) : null}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Material:
                            </Typography>
                            <Typography variant="body2">
                              {startingMaterial.materials && startingMaterial.materials.length > 0 ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: startingMaterial.materials.join(', '),
                                  }}
                                />
                              ) : startingMaterial.substrateMaterials &&
                                startingMaterial.substrateMaterials.length > 0 ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: startingMaterial.substrateMaterials.join(', '),
                                  }}
                                />
                              ) : (
                                '(not specified)'
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewStartingMaterial(startingMaterial)}
                          sx={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          }}
                        >
                          View Details
                        </Button>
                        <Link
                          href={`${systemConfig?.basePath}/samples/${startingMaterial.sampleLabel}`}
                          passHref
                          style={{ textDecoration: 'none' }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<ArrowForward />}
                            sx={{
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            }}
                          >
                            Go to Sample
                          </Button>
                        </Link>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </>
          ) : null}

          {techniqueUsesSubstrates &&
          startingMaterials.length === 0 &&
          (!isReadOnly || sampleHasSubstrateDetailsData(sample)) ? (
            <>
              <Typography variant="h6" gutterBottom>
                Substrate Details
              </Typography>
              <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {/* Name */}
                  {sample.substrateName || !isReadOnly ? (
                    <DetailField label="Name" value={sample.substrateName} isReadOnly={isReadOnly || isSubmitted}>
                      <Autocomplete
                        options={substrateNameOptions}
                        value={sample.substrateName}
                        onInputChange={(_, newInputValue) => {
                          handleModification({ substrateName: newInputValue });
                        }}
                        onChange={(_, newValue, reason) => {
                          const name = typeof newValue === 'string' ? newValue : undefined;
                          if (reason === 'selectOption' && name != null && substrateRows?.length) {
                            const matches = filterSubstrateRows(substrateRows, { substrateName: name });
                            const row = pickRepresentativeSubstrateRow(matches);
                            handleModification({
                              substrateName: name,
                              ...(row ? samplePatchFromSubstrateRow(row) : {}),
                            });
                            return;
                          }
                          handleModification({ substrateName: name });
                        }}
                        freeSolo
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </DetailField>
                  ) : null}

                  {/* Vendor */}
                  {sample.substrateVendor || !isReadOnly ? (
                    <DetailField label="Vendor" value={sample.substrateVendor} isReadOnly={isReadOnly || isSubmitted}>
                      <Autocomplete
                        options={substrateVendorOptions}
                        value={sample.substrateVendor}
                        onInputChange={(_, newInputValue) => {
                          handleModification({ substrateVendor: newInputValue });
                        }}
                        onChange={(_, newValue, reason) => {
                          const v = typeof newValue === 'string' ? newValue : undefined;
                          if (reason === 'selectOption' && v != null && substrateRows?.length) {
                            const patch = applySubstrateLookupIfUnique(
                              filterSubstrateRows(substrateRows, {
                                substrateVendor: v,
                                substrateName: sample.substrateName,
                              }),
                              { substrateVendor: v }
                            );
                            if (patch) {
                              handleModification(patch);
                              return;
                            }
                          }
                          handleModification({ substrateVendor: v });
                        }}
                        freeSolo
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </DetailField>
                  ) : null}

                  {/* Lot */}
                  {sample.substrateLot || !isReadOnly ? (
                    <DetailField label="Lot" value={sample.substrateLot} isReadOnly={isReadOnly || isSubmitted}>
                      <Autocomplete
                        options={substrateLotOptions}
                        value={sample.substrateLot}
                        onInputChange={(_, newInputValue) => {
                          handleModification({ substrateLot: newInputValue });
                        }}
                        onChange={(_, newValue, reason) => {
                          const lot = typeof newValue === 'string' ? newValue : undefined;
                          if (reason === 'selectOption' && lot != null && substrateRows?.length) {
                            const patch = applySubstrateLookupIfUnique(
                              filterSubstrateRows(substrateRows, {
                                substrateLot: lot,
                                substrateName: sample.substrateName,
                                substrateVendor: sample.substrateVendor,
                              }),
                              { substrateLot: lot }
                            );
                            if (patch) {
                              handleModification(patch);
                              return;
                            }
                          }
                          handleModification({ substrateLot: lot });
                        }}
                        freeSolo
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </DetailField>
                  ) : null}

                  {/* Orientation */}
                  {sample.substrateOrientation || !isReadOnly ? (
                    <DetailField
                      label="Orientation"
                      value={sample.substrateOrientation}
                      isReadOnly={isReadOnly || isSubmitted}
                    >
                      <Autocomplete
                        options={substrateOrientationOptions}
                        value={sample.substrateOrientation || ''}
                        onInputChange={(_, newInputValue) => {
                          handleModification({ substrateOrientation: newInputValue });
                        }}
                        onChange={(_, newValue, reason) => {
                          const orientation = typeof newValue === 'string' ? newValue : undefined;
                          if (reason === 'selectOption' && orientation != null && substrateRows?.length) {
                            const patch = applySubstrateLookupIfUnique(
                              filterSubstrateRows(substrateRows, {
                                substrateOrientation: orientation,
                                substrateName: sample.substrateName,
                                substrateVendor: sample.substrateVendor,
                                substrateLot: sample.substrateLot,
                              }),
                              { substrateOrientation: orientation }
                            );
                            if (patch) {
                              handleModification(patch);
                              return;
                            }
                          }
                          handleModification({ substrateOrientation: orientation });
                        }}
                        freeSolo
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </DetailField>
                  ) : null}

                  {/* Purity */}
                  {sample.substratePurity !== undefined || !isReadOnly ? (
                    <DetailField
                      label="Purity"
                      value={`${sample.substratePurity}%`}
                      isReadOnly={isReadOnly || isSubmitted}
                    >
                      <TextField
                        type="number"
                        value={sample.substratePurity?.toString() ?? ''}
                        onChange={(e) => {
                          const newPurity = e.target.value === '' ? undefined : Number(e.target.value);
                          handleModification({ substratePurity: newPurity });
                        }}
                        fullWidth
                        size="small"
                        //FIXME: Inputprops is depcreated
                        InputProps={{
                          readOnly: isReadOnly,
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    </DetailField>
                  ) : null}

                  {/* Substrate Material - Show as "Material" if no growth material, otherwise "Substrate Material" */}
                  {sample.substrateMaterialIds?.length || !isReadOnly ? (
                    <DetailField
                      label={
                        Array.isArray(sample.materialIds) && sample.materialIds.length > 0
                          ? 'Substrate Material'
                          : 'Material'
                      }
                      value={
                        <span
                          dangerouslySetInnerHTML={{
                            __html:
                              sample.substrateMaterialIds?.map((id) => getMaterialName(Number(id))).join(', ') ??
                              'Not specified',
                          }}
                        />
                      }
                      required
                      isReadOnly={isReadOnly || isSubmitted}
                    >
                      <Autocomplete
                        options={materialOptions ?? []}
                        filterOptions={(opts, params) =>
                          filterAutocompleteOptionsByMatchRank(opts, params, (o) =>
                            typeof o === 'string' ? o : o.name
                          )
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option.id ?? option.name}>
                            {option.name}
                          </li>
                        )}
                        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={
                          (sample.substrateMaterialIds ?? []).length
                            ? (materialOptions?.find((m) => m.id === (sample.substrateMaterialIds ?? [])[0]) ?? null)
                            : null
                        }
                        onChange={(_, selectedOption) => {
                          const selectedId = typeof selectedOption === 'string' ? undefined : selectedOption?.id;

                          handleModification({
                            substrateMaterialIds: selectedId ? [selectedId] : [],
                          });
                        }}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </DetailField>
                  ) : null}
                </Box>
              </Paper>
            </>
          ) : null}
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Footer
          lastChangedDate={sample.lastChangedDate}
          lastChangedBy={resolveUser(sample.userId || '')}
          status={sample.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => setIsManuallyEditing(!val)}
          onSave={handleSave}
          privilege={PrivilegeEnum.Sample}
          disableDelete={sample.hasSubmittedActivities}
          privilegeContext={
            sample.id != null || sample.sampleLabel
              ? { entityType: 'sample', entityId: String(sample.id ?? sample.sampleLabel) }
              : undefined
          }
          canUpdate={sample.canUpdateSample}
        />
      </Box>

      {/* StartingMaterial Details Modal */}
      <StartingMaterialDetailsDialog
        open={startingMaterialModalOpen}
        startingMaterial={selectedStartingMaterial}
        onClose={handleCloseStartingMaterialModal}
      />
    </Box>
  );
};

export default SampleDetails;
